import React, { useState } from 'react';
import { Play, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { testValidationRule, type ValidationRule } from '../../../services/queries/validationRuleQueries';
import { useToast } from '../../../contexts/ToastContext';

interface ValidationRuleTesterProps {
  rule: Partial<ValidationRule>;
  allFields: any[];
}

export const ValidationRuleTester: React.FC<ValidationRuleTesterProps> = ({ rule, allFields }) => {
  const { warning } = useToast();
  const [testData, setTestData] = useState<Record<string, any>>({});
  const [testResult, setTestResult] = useState<{ passes: boolean; errorMessage?: string } | null>(null);
  const [hasRun, setHasRun] = useState(false);

  // Get fields used in conditions
  const relevantFields = allFields.filter(field =>
    rule.conditions?.some(c => c.fieldId === field.id)
  );

  const handleRunTest = () => {
    if (!rule.conditions || rule.conditions.length === 0) {
      warning('No conditions to test', 'Please add at least one condition before testing');
      return;
    }

    const result = testValidationRule(rule as ValidationRule, testData);
    setTestResult(result);
    setHasRun(true);
  };

  const handleReset = () => {
    setTestData({});
    setTestResult(null);
    setHasRun(false);
  };

  return (
    <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-800 p-4">
      <div className="flex items-center gap-2 mb-4">
        <Play className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-300">
          Test Validation Rule
        </h3>
      </div>

      <p className="text-xs text-blue-800 dark:text-blue-400 mb-4">
        Enter test values to see if this rule would trigger an error
      </p>

      {/* Test Fields */}
      {relevantFields.length > 0 ? (
        <div className="space-y-3 mb-4">
          {relevantFields.map(field => (
            <div key={field.id}>
              <label className="block text-xs font-medium text-blue-900 dark:text-blue-300 mb-1">
                {field.label}
              </label>
              {field.type === 'checkbox' ? (
                <select
                  value={String(testData[field.id] ?? '')}
                  onChange={(e) => setTestData(prev => ({
                    ...prev,
                    [field.id]: e.target.value === 'true'
                  }))}
                  className="w-full px-3 py-2 text-sm border border-blue-300 dark:border-blue-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="">-- Select --</option>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              ) : field.type === 'picklist' ? (
                <select
                  value={String(testData[field.id] ?? '')}
                  onChange={(e) => setTestData(prev => ({ ...prev, [field.id]: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-blue-300 dark:border-blue-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="">-- Select --</option>
                  {field.options?.map((opt: any) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={
                    field.type === 'number' || field.type === 'currency'
                      ? 'number'
                      : field.type === 'date'
                      ? 'date'
                      : field.type === 'email'
                      ? 'email'
                      : 'text'
                  }
                  value={testData[field.id] ?? ''}
                  onChange={(e) => setTestData(prev => ({
                    ...prev,
                    [field.id]: field.type === 'number' || field.type === 'currency'
                      ? Number(e.target.value)
                      : e.target.value
                  }))}
                  placeholder={`Enter ${field.label.toLowerCase()}...`}
                  className="w-full px-3 py-2 text-sm border border-blue-300 dark:border-blue-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 mb-4">
          <AlertCircle className="w-8 h-8 text-blue-400 mx-auto mb-2" />
          <p className="text-xs text-blue-700 dark:text-blue-400">
            Add conditions to enable testing
          </p>
        </div>
      )}

      {/* Test Result */}
      {hasRun && testResult && (
        <div className={`mb-4 p-3 rounded-lg border ${
          testResult.passes
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
        }`}>
          <div className="flex items-start gap-2">
            {testResult.passes ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-green-900 dark:text-green-300">
                    ✓ Validation Passed
                  </p>
                  <p className="text-xs text-green-800 dark:text-green-400 mt-1">
                    Record would be saved successfully
                  </p>
                </div>
              </>
            ) : (
              <>
                <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-900 dark:text-red-300">
                    ✗ Validation Failed
                  </p>
                  <p className="text-xs text-red-800 dark:text-red-400 mt-1">
                    {testResult.errorMessage}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2">
        <button
          onClick={handleRunTest}
          disabled={relevantFields.length === 0}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          <Play className="w-4 h-4" />
          Run Test
        </button>
        {hasRun && (
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
};
